import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, lazy, Suspense } from 'react';
import { Toaster } from '@/components/ui/sonner';
import LandingPage from './pages/LandingPage';
import { Card } from '@/components/ui/card';
import '@/index.css';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Exchanges = lazy(() => import('./pages/Exchanges'));
const Bots = lazy(() => import('./pages/Bots'));
const Opportunities = lazy(() => import('./pages/Opportunities'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Demo = lazy(() => import('./pages/Demo'));
const Billing = lazy(() => import('./pages/Billing'));
const Support = lazy(() => import('./pages/Support'));
const Backtesting = lazy(() => import('./pages/Backtesting'));
const RiskMonitor = lazy(() => import('./pages/RiskMonitor'));
const SystemHealth = lazy(() => import('./pages/SystemHealth'));
const StressTesting = lazy(() => import('./pages/StressTesting'));
const PerformanceMetrics = lazy(() => import('./pages/PerformanceMetrics'));
const ValidationDashboard = lazy(() => import('./pages/ValidationDashboard'));
const QuantAudit = lazy(() => import('./pages/QuantAudit'));
const AutomatedTrading = lazy(() => import('./pages/AutomatedTrading'));
const SMCAnalysis = lazy(() => import('./pages/SMCAnalysis'));
const MultiTimeframeAnalysis = lazy(() => import('./pages/MultiTimeframeAnalysis'));
const OnChainIntelligence = lazy(() => import('./pages/OnChainIntelligence'));
const NewsIntelligence = lazy(() => import('./pages/NewsIntelligence'));
const AINewsUnderstanding = lazy(() => import('./pages/AINewsUnderstanding'));
const MacroIntelligence = lazy(() => import('./pages/MacroIntelligence'));
const MarketSentiment = lazy(() => import('./pages/MarketSentiment'));
const WhaleTracker = lazy(() => import('./pages/WhaleTracker'));
const OrderFlow = lazy(() => import('./pages/OrderFlow'));
const TradeConfidence = lazy(() => import('./pages/TradeConfidence'));
const ModelLearning = lazy(() => import('./pages/ModelLearning'));
const Orders = lazy(() => import('./pages/Orders'));
const Risk = lazy(() => import('./pages/Risk'));
const Strategies = lazy(() => import('./pages/Strategies'));
const TradeLifecycle = lazy(() => import('./pages/TradeLifecycle'));
const TradeAnalysisViewer = lazy(() => import('./pages/TradeAnalysisViewer'));
const SignalPerformance = lazy(() => import('./pages/SignalPerformance'));
const OpportunitiesPanel = lazy(() => import('./pages/OpportunitiesPanel'));
const PerformanceReport = lazy(() => import('./pages/PerformanceReport'));
const Compliance = lazy(() => import('./pages/Compliance'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Card className="bg-card border-border p-8">
      <div className="text-foreground text-center">
        <div className="animate-pulse mb-4">⚡</div>
        Loading...
      </div>
    </Card>
  </div>
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const PrivateRoute = ({ children, adminOnly }) => {
    if (loading) return <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-foreground">Loading...</div>
    </div>;
    if (!user) return <Navigate to="/" />;
    if (adminOnly && !user.is_admin) return <Navigate to="/dashboard" />;
    return children;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground">Loading Arbex...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage setUser={setUser} user={user} />} />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Dashboard user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/exchanges"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Exchanges user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/bots"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Bots user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/opportunities"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Opportunities user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Analytics user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Settings user={user} setUser={setUser} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <PrivateRoute adminOnly>
                <Suspense fallback={<PageLoader />}>
                  <AdminDashboard user={user} setUser={setUser} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/compliance"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Compliance user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/demo"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Demo user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Billing user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/support"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Support user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/backtesting"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Backtesting user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/strategies"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Strategies user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/risk-monitor"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <RiskMonitor user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/health"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <SystemHealth user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/stress-testing"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <StressTesting user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/performance-metrics"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <PerformanceMetrics user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/validation"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <ValidationDashboard user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/audit"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <QuantAudit user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/auto-trading"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <AutomatedTrading user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/smc-analysis"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <SMCAnalysis user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/multi-timeframe"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <MultiTimeframeAnalysis user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/onchain"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <OnChainIntelligence user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/news"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <NewsIntelligence user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/ai-news"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <AINewsUnderstanding user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/macro"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <MacroIntelligence user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/sentiment"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <MarketSentiment user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/whales"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <WhaleTracker user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/orderflow"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <OrderFlow user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/confidence"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <TradeConfidence user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/model-learning"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <ModelLearning user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Orders user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/risk"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <Risk user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/trade-lifecycle"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <TradeLifecycle user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics/trade/:tradeId"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <TradeAnalysisViewer user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics/signals"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <SignalPerformance user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics/opportunities"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <OpportunitiesPanel user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
          <Route
            path="/analytics/report"
            element={
              <PrivateRoute>
                <Suspense fallback={<PageLoader />}>
                  <PerformanceReport user={user} />
                </Suspense>
              </PrivateRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;